import _ from "lodash";
import { HOOPS_STACK_MARKERS, IGNORED_EXCEPTION_MESSAGES, isHoopsError } from "./src/sharedErrorConstants";

const IGNORED_NETWORK_ERRORS = ["ERR_NETWORK", "ERR_CANCELED", "ECONNABORTED"];
const IGNORED_FETCH_ERROR_MESSAGES = ["Network request failed"];

/**
 * Scrub CP-283 external-share credentials out of anything Sentry ships.
 *
 * The share URL path segment IS the credential (`/share/<token>/...`), and the byte URLs carry
 * one-shot HMAC tickets (`?ticket=...`). Sentry attaches `event.request.url`, breadcrumb URLs
 * (navigation + xhr/fetch), transaction names and span descriptions automatically, so without scrubbing every captured
 * error on a recipient page uploads a replayable link to a third-party SaaS — exactly what PRD 5's
 * "keep the URL out of any log" forbids.
 */
const SHARE_TOKEN_PATTERN = /\/share\/[^/?#\s"']+/g;
const SHARE_TICKET_PATTERN = /([?&]ticket=)[^&#\s"']+/g;

function scrubShareSecrets(value) {
  if (typeof value !== "string") return value;
  return value.replace(SHARE_TOKEN_PATTERN, "/share/[redacted]").replace(SHARE_TICKET_PATTERN, "$1[redacted]");
}

/**
 * Every string anywhere in the event rather than a list of known fields: transactions and spans carry
 * the path in names, descriptions and attributes, breadcrumbs under `data` keys that vary by type, and
 * a field list is how transaction events came to ship the token unscrubbed. `sdkProcessingMetadata`
 * is skipped: it holds the SDK's live scopes, is never sent, and is not ours to rewrite.
 */
export function scrubShareEvent(event) {
  const seen = new WeakSet();
  const walk = (node) => {
    if (!node || typeof node !== "object" || seen.has(node)) return;
    seen.add(node);
    for (const key of Object.keys(node)) {
      if (key === "sdkProcessingMetadata") continue;
      const value = node[key];
      if (typeof value === "string") {
        const scrubbed = scrubShareSecrets(value);
        if (scrubbed !== value) node[key] = scrubbed;
      } else {
        walk(value);
      }
    }
  };
  walk(event);
  return event;
}

export function sentryBeforeSend(event, hint) {
  scrubShareEvent(event);

  const networkError = _.get(hint, "originalException.code");
  const exceptionMessage = _.get(hint, "originalException.message") || _.get(hint, "originalException.value");
  const isFetchNetworkError =
    _.get(hint, "originalException.name") === "TypeError" && IGNORED_FETCH_ERROR_MESSAGES.includes(exceptionMessage);
  if (
    IGNORED_NETWORK_ERRORS.includes(networkError) ||
    isFetchNetworkError ||
    _.get(hint, "originalException.status") == "401" ||
    (_.get(hint, "originalException.status") == "403" &&
      _.get(hint, "originalException.response.data.errorCode") === 1001)
  ) {
    return null;
  }

  const exceptions = _.get(event, "exception.values", []);
  const candidateMessages = [
    exceptionMessage,
    _.get(event, "message"),
    ..._.map(exceptions, (ex) => _.get(ex, "value")),
  ];
  const isIgnoredMessage = _.some(
    candidateMessages,
    (message) => _.isString(message) && _.some(IGNORED_EXCEPTION_MESSAGES, (ignored) => message.includes(ignored)),
  );
  if (isIgnoredMessage) {
    return null;
  }

  const originalException = _.get(hint, "originalException");
  if (_.isObject(originalException) && Object.hasOwn(originalException, "scFunction")) {
    return null;
  }
  const hasHoopsFrame = _.some(exceptions, (ex) =>
    _.some(_.get(ex, "stacktrace.frames", []), (frame) =>
      _.some(HOOPS_STACK_MARKERS, (marker) => _.includes(_.get(frame, "filename"), marker)),
    ),
  );
  if ((hasHoopsFrame || isHoopsError(originalException)) && !globalThis.location.href.includes("designviewer")) {
    return null;
  }

  const isFabricSchedulerCrash = _.some(
    exceptions,
    (ex) =>
      _.get(ex, "type") === "EXC_BAD_ACCESS" &&
      _.some(
        _.get(ex, "stacktrace.frames", []),
        (frame) =>
          frame && frame.filename === "Scheduler.cpp" && _.includes(frame.function, "uiManagerDidFinishTransaction"),
      ),
  );
  if (isFabricSchedulerCrash) {
    return null;
  }

  return event;
}
